import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { SocialLoginStatus, SocialStatus } from "../../components/common/SocialLoginStatus";
import { useTranslation } from "react-i18next";

const OAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<SocialStatus>('processing');
  const [showManualClose, setShowManualClose] = useState(false);
  const { t } = useTranslation('common');

  const handleManualClose = useCallback(() => {
    window.close();
  }, []);

  const channelId = searchParams.get("channelId");
  const providerParam = searchParams.get("provider");
  const connectionName = searchParams.get("connectionName");
  const providerName = providerParam || channelId || "service";

  useEffect(() => {
    // Extract OAuth parameters from URL
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    // Check for backend-initiated success (e.g. Shopify or Social Login)
    const oauthStatus = searchParams.get("status");
    const channelId = searchParams.get("channelId");
    const provider = searchParams.get("provider");
    const token = searchParams.get("token");

    const sendMessage = (message: any) => {
      console.log("OAuthCallbackPage: Sending message", message);

      if (window.opener) {
        try {
          window.opener.postMessage(message, window.location.origin);
          console.log("OAuthCallbackPage: Message sent via postMessage");
        } catch (e) {
          console.error("OAuthCallbackPage: Failed to send postMessage", e);
        }
      }

      try {
        localStorage.setItem(
          "oauth_callback_result",
          JSON.stringify({
            ...message,
            timestamp: Date.now(),
          })
        );
        console.log("OAuthCallbackPage: Wrote to localStorage");
      } catch (e) {
        console.error("OAuthCallbackPage: Failed to write to localStorage", e);
      }
    };

    if (oauthStatus === "success") {
      setStatus('success');
      sendMessage({
        type: "oauth-success",
        channelId,
        connectionName,
        provider,
        token
      });

      // Auto-close only on success
      const autoCloseTimer = setTimeout(() => {
        window.close();
        // If window didn't close, show manual button after another brief delay
        setTimeout(() => setShowManualClose(true), 1000);
      }, 1000);

      return () => clearTimeout(autoCloseTimer);
    } else if (error) {
      setStatus('error');
      sendMessage({
        type: "oauth-error",
        error,
        errorDescription,
      });
      setShowManualClose(true);
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-neutral-950 p-6">
      <div className="w-full max-w-lg">
        <SocialLoginStatus 
          status={status} 
          provider={providerName} 
          variant="page"
          errorMessage={searchParams.get("error_description") || undefined}
        />

        <AnimatePresence>
          {showManualClose && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 text-center"
            >
              <button
                onClick={handleManualClose}
                className="inline-flex items-center justify-center px-8 py-3.5 bg-neutral-900 dark:bg-white text-white dark:text-black font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all active:scale-95 text-sm"
              >
                Close This Window
              </button>
              <p className="mt-4 text-[11px] text-neutral-400 font-bold uppercase tracking-wider">
                {t('oauth.safeToClose', 'Safe to close now')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Minimal Footer */}
        <div className="fixed bottom-8 left-0 right-0 text-center">
          <p className="text-[10px] text-neutral-300 dark:text-neutral-800 uppercase tracking-[0.3em] font-black pointer-events-none">
            {t('oauth.secureGateway', 'Dashbal Secure Gateway')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
